import { useEffect, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Button, InfoBox, Input, Modal, NativeSelect, Skeleton, StatusBadge, Switch } from '@immo/ui-kit';
import {
  FACT_KEY_GROUPS,
  type FactKeyDetailView,
  type FactKeyGroup,
  type FactKeyView,
  type FactScope,
  PROPERTY_FIELD_LINK_KEYS,
  type PropertyCategory,
  type PropertyFieldLinkOverride,
  type UpdateFactKeyBody,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { FactKeyCategoryPicker } from '@/pages/skills/fact-key-category-picker';
import { FactKeyNeedRows } from '@/pages/skills/fact-key-need-rows';
import {
  conflictOf,
  duplicateOf,
  normaliseNeedRows,
  sameNeeds,
  toDraftRows,
  withoutId,
  type AmbiguousDetails,
  type DraftNeedRow,
  type NeedConflict,
} from '@/pages/skills/fact-key-needs';
import { RetireFactKeyDialog } from '@/pages/skills/retire-fact-key-dialog';

const SCOPES: ReadonlyArray<FactScope> = ['PROPERTY_INTRINSIC', 'DEAL_CONTEXT'];
const NOT_FOUND_CODE = 'property.fact-key.not-found';
const SOURCE_MESSAGE_PREFIX = 'pages.admin.skills.fact-keys.source.';
const PERSONAL_DATA_CODE = 'property.fact-key.personal-data-not-public';
const INCOMPLETE_CODE = 'property.fact-key.incomplete';
const NEED_AMBIGUOUS_CODE = 'property.fact-key.need-ambiguous';
const RETIRED_HAS_NO_NEED_CODE = 'property.fact-key.retired-has-no-need';
const SHOWN_CODES = new Set([PERSONAL_DATA_CODE, INCOMPLETE_CODE, RETIRED_HAS_NO_NEED_CODE]);

type Draft = {
  readonly label: string;
  readonly scope: FactScope;
  readonly publicSafe: boolean;
  readonly propertyFieldKey: string;
  readonly groupId: FactKeyGroup;
  readonly categories: ReadonlyArray<PropertyCategory>;
  readonly needs: ReadonlyArray<DraftNeedRow>;
};

type Read =
  | { readonly kind: 'loading' }
  | { readonly kind: 'not-found' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'ready'; readonly detail: FactKeyDetailView };

const draftOf = (detail: FactKeyDetailView): Draft => ({
  label: detail.label,
  scope: detail.scope,
  publicSafe: detail.publicSafe,
  propertyFieldKey: detail.propertyFieldKey ?? '',
  groupId: detail.groupId,
  categories: detail.categories,
  needs: toDraftRows(detail.needs),
});

const effectiveScope = (draft: Draft): FactScope => (draft.groupId === 'VMK' ? 'DEAL_CONTEXT' : draft.scope);

function diff(draft: Draft, detail: FactKeyDetailView): UpdateFactKeyBody {
  const label = draft.label.trim();
  const propertyFieldKey = draft.propertyFieldKey === '' ? null : draft.propertyFieldKey;
  const scope = effectiveScope(draft);

  return {
    ...(label !== detail.label ? { label } : {}),
    ...(scope !== detail.scope ? { scope } : {}),
    ...(draft.publicSafe !== detail.publicSafe ? { publicSafe: draft.publicSafe } : {}),
    ...(propertyFieldKey !== detail.propertyFieldKey
      ? { propertyFieldKey: propertyFieldKey as UpdateFactKeyBody['propertyFieldKey'] }
      : {}),
    ...(draft.groupId !== detail.groupId ? { groupId: draft.groupId } : {}),
    ...(draft.categories.join() !== detail.categories.join() ? { categories: [...draft.categories] } : {}),
    ...(!sameNeeds(draft.needs.map(withoutId), detail.needs)
      ? { needs: normaliseNeedRows(draft.needs.map(withoutId)) }
      : {}),
  };
}

type EntryModalProps = {
  readonly row: FactKeyView;
  readonly canManage: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly onRetired: () => void;
};

export function FactKeyEntryModal({ row, canManage, onClose, onSaved, onRetired }: EntryModalProps) {
  const t = useTranslations('pages.admin.skills.fact-keys');
  const tErrors = useTranslations('errors');
  const [read, setRead] = useState<Read>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;

    setRead({ kind: 'loading' });
    void apiClient.adminFactKeys.get({ pathParams: { key: row.key } }).then((result) => {
      if (cancelled) return;

      if (result.ok) setRead({ kind: 'ready', detail: result.body });
      else setRead({ kind: result.status === 404 ? 'not-found' : 'failed' });
    });

    return () => {
      cancelled = true;
    };
  }, [row.key]);

  if (read.kind === 'ready') {
    const loaded = read.detail;

    return (
      <EntryForm
        key={`${loaded.key}${loaded.updatedAt}`}
        detail={loaded}
        canManage={canManage}
        onClose={onClose}
        onSaved={onSaved}
        onRetired={(view) => {
          setRead({ kind: 'ready', detail: { ...loaded, ...view } });
          onRetired();
        }}
      />
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      placement="right"
      size="lg"
      title={row.label}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            {canManage ? t('entry.cancel') : t('entry.close')}
          </Button>
          {canManage && (
            <Button type="button" disabled>
              {t('entry.save')}
            </Button>
          )}
        </>
      }
    >
      <EntryHeader row={row} />
      {read.kind === 'loading' && (
        <div className="mt-4 space-y-3" aria-hidden>
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}
      {read.kind === 'not-found' && (
        <div className="mt-4">
          <InfoBox type="danger">{tErrors(NOT_FOUND_CODE)}</InfoBox>
        </div>
      )}
      {read.kind === 'failed' && (
        <div className="mt-4">
          <InfoBox type="danger">{t('entry.load-error')}</InfoBox>
        </div>
      )}
    </Modal>
  );
}

function EntryHeader({ row }: { readonly row: FactKeyView }) {
  const t = useTranslations('pages.admin.skills.fact-keys');

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary">
      <span className="font-mono text-xs">{row.key}</span>
      <StatusBadge
        size="sm"
        dot
        tone={row.status === 'ACTIVE' ? 'success' : 'neutral'}
        label={t(`status.${row.status}`)}
      />
      <span>{t(`origin.${row.origin}`)}</span>
    </div>
  );
}

type EntryFormProps = Omit<EntryModalProps, 'row' | 'onRetired'> & {
  readonly detail: FactKeyDetailView;
  readonly onRetired: (view: FactKeyView) => void;
};

function EntryForm({ detail, canManage, onClose, onSaved, onRetired }: EntryFormProps) {
  const t = useTranslations('pages.admin.skills.fact-keys');
  const tErrors = useTranslations('errors');
  const tCategory = useTranslations('common.immobilientyp.category');
  const tNeeds = useTranslations('pages.admin.skills.fact-keys.needs');
  const overrideCondition = (override: PropertyFieldLinkOverride): string =>
    [
      override.marketingType === null ? null : tNeeds(`marketing-type.${override.marketingType}`),
      override.category === null ? null : tCategory(override.category),
    ]
      .filter((part) => part !== null)
      .join(' · ');
  const [draft, setDraftState] = useState<Draft>(() => draftOf(detail));
  const [conflict, setConflict] = useState<NeedConflict | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [attempted, setAttempted] = useState(false);

  const setDraft = (next: Draft) => {
    setConflict(null);
    setDraftState(next);
  };

  const body = diff(draft, detail);
  const unchanged = Object.keys(body).length === 0;
  const canSave = !saving && !unchanged;
  const scope = effectiveScope(draft);
  const labelBlank = attempted && draft.label.trim() === '';
  const noCategory = attempted && draft.categories.length === 0;

  const send = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);

    try {
      const result = await apiClient.adminFactKeys.update({ pathParams: { key: detail.key }, body });

      if (result.ok) {
        onSaved();

        return;
      }

      const error = (result.body as { error?: { code?: string; details?: AmbiguousDetails } } | undefined)?.error;
      const code = error?.code;
      const refused =
        code === NEED_AMBIGUOUS_CODE && error?.details !== undefined
          ? conflictOf(error.details, draft.needs, detail.needs)
          : null;

      if (refused !== null) {
        setConflict(refused);

        return;
      }

      setSaveError(code !== undefined && SHOWN_CODES.has(code) ? tErrors(code) : t('save-error'));
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    setAttempted(true);

    if (draft.label.trim() === '' || draft.categories.length === 0) {
      setSaveError(tErrors(INCOMPLETE_CODE));

      return;
    }

    const duplicate = duplicateOf(draft.needs);

    if (duplicate !== null) {
      setConflict(duplicate);

      return;
    }

    if (body.publicSafe === true) {
      setConfirming(true);

      return;
    }

    void send();
  };

  const confirmPublicSafe = () => {
    setConfirming(false);
    void send();
  };

  return (
    <>
      <Modal
        open
        onClose={onClose}
        placement="right"
        size="lg"
        title={detail.label}
        footer={
          canManage ? (
            <>
              {detail.status === 'ACTIVE' && (
                <Button type="button" variant="danger" className="sm:mr-auto" onClick={() => setRetireOpen(true)}>
                  {t('retire.button')}
                </Button>
              )}
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('entry.cancel')}
              </Button>
              <Button type="button" disabled={!canSave} onClick={save}>
                {t('entry.save')}
              </Button>
            </>
          ) : (
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('entry.close')}
            </Button>
          )
        }
      >
        <div className="space-y-5">
          <EntryHeader row={detail} />
          {saveError !== null && <InfoBox type="danger">{saveError}</InfoBox>}

          <div className="grid grid-cols-2 gap-4">
            <Field label={t('columns.label')} htmlFor="fact-key-label">
              {canManage ? (
                <Input
                  id="fact-key-label"
                  value={draft.label}
                  invalid={labelBlank}
                  onChange={(event) => setDraft({ ...draft, label: event.target.value })}
                />
              ) : (
                <span>{detail.label}</span>
              )}
            </Field>

            <Field label={t('columns.group')} htmlFor="fact-key-group">
              {canManage ? (
                <NativeSelect
                  id="fact-key-group"
                  value={draft.groupId}
                  onChange={(value) => setDraft({ ...draft, groupId: value as FactKeyGroup })}
                  options={FACT_KEY_GROUPS.map((group) => ({ value: group, label: t(`group.${group}`) }))}
                />
              ) : (
                <span>{t(`group.${detail.groupId}`)}</span>
              )}
            </Field>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <Fact label={t('columns.key')}>
              <span className="font-mono text-xs">{detail.key}</span>
            </Fact>
            <Fact label={t('columns.value-type')}>{t(`value-type.${detail.valueType}`)}</Fact>
            <Fact label={t('columns.frequency')}>{t(`frequency.${detail.frequency}`)}</Fact>
            <Fact label={t('columns.origin')}>{t(`origin.${detail.origin}`)}</Fact>
            <Fact label={t('columns.status')}>{t(`status.${detail.status}`)}</Fact>
            {detail.parentKey !== null && (
              <Fact label={t('entry.parent')}>
                <span className="font-mono text-xs">{detail.parentKey}</span>
              </Fact>
            )}
            {detail.valueType === 'number' && (
              <Fact label={t('entry.number-style')}>
                {detail.numberStyle === null ? '' : t(`number-style.${detail.numberStyle}`)}
              </Fact>
            )}
            {detail.valueType === 'number' && detail.unit !== null && (
              <Fact label={t('entry.unit')}>{detail.unit}</Fact>
            )}
          </dl>

          {canManage ? (
            <FactKeyCategoryPicker
              value={draft.categories}
              invalid={noCategory}
              onChange={(categories) =>
                setDraft({
                  ...draft,
                  categories,
                  needs: draft.needs.filter((row) => categories.includes(row.category)),
                })
              }
            />
          ) : (
            <Field label={t('columns.categories')}>
              <span>{detail.categories.map((category) => tCategory(category)).join(', ')}</span>
            </Field>
          )}

          {detail.status === 'ACTIVE' && (
            <Field label={t('entry.needs-heading')}>
              <FactKeyNeedRows
                categories={draft.categories}
                rows={draft.needs}
                readOnly={!canManage}
                conflict={conflict}
                onChange={(needs) => setDraft({ ...draft, needs })}
              />
            </Field>
          )}

          <Field label={t('columns.scope')} htmlFor="fact-key-scope">
            {canManage ? (
              <NativeSelect
                id="fact-key-scope"
                value={scope}
                disabled={draft.groupId === 'VMK'}
                onChange={(value) => setDraft({ ...draft, scope: value as FactScope })}
                options={SCOPES.map((scope) => ({ value: scope, label: t(`scope.${scope}`) }))}
              />
            ) : (
              <span>{t(`scope.${detail.scope}`)}</span>
            )}
          </Field>

          <Field label={t('columns.public-safe')}>
            {canManage ? (
              <Switch
                checked={draft.publicSafe}
                disabled={detail.personalData}
                label={t('columns.public-safe')}
                onCheckedChange={(publicSafe) => setDraft({ ...draft, publicSafe })}
              />
            ) : (
              <span>{t(`yes-no.${String(detail.publicSafe)}`)}</span>
            )}
            {detail.personalData && <p className="text-sm text-text-tertiary">{t('entry.personal-data-hint')}</p>}
          </Field>

          <Field label={t('columns.property-field')} htmlFor="fact-key-property-field">
            {canManage ? (
              <NativeSelect
                id="fact-key-property-field"
                value={draft.propertyFieldKey}
                onChange={(propertyFieldKey) => setDraft({ ...draft, propertyFieldKey })}
                options={[
                  { value: '', label: t('property-field-none') },
                  ...PROPERTY_FIELD_LINK_KEYS.map((fieldKey) => ({ value: fieldKey, label: fieldKey })),
                ]}
              />
            ) : (
              <span>{detail.propertyFieldKey ?? t('property-field-none')}</span>
            )}
            {detail.propertyFieldKeyOverrides.map((override) => (
              <p
                key={`${override.marketingType}-${override.category}-${override.propertyFieldKey}`}
                className="text-sm text-text-tertiary"
              >
                {overrideCondition(override)}: {override.propertyFieldKey}
              </p>
            ))}
          </Field>

          <DetailsSection detail={detail} />
        </div>
      </Modal>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        size="sm"
        stacked
        title={t('public-safe-confirm.title')}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
              {t('public-safe-confirm.cancel')}
            </Button>
            <Button type="button" variant="danger" onClick={confirmPublicSafe}>
              {t('public-safe-confirm.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('public-safe-warning')}</p>
      </Modal>

      {retireOpen && (
        <RetireFactKeyDialog
          detail={detail}
          onClose={() => setRetireOpen(false)}
          onRetired={(view) => {
            setRetireOpen(false);
            onRetired(view);
          }}
        />
      )}
    </>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  readonly label: string;
  readonly htmlFor?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="space-y-1 text-sm">
      {htmlFor === undefined ? (
        <div className="text-text-tertiary">{label}</div>
      ) : (
        <label htmlFor={htmlFor} className="block text-text-tertiary">
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div>
      <dt className="text-text-tertiary">{label}</dt>
      <dd className="text-text-primary">{children}</dd>
    </div>
  );
}

function DetailsSection({ detail }: { readonly detail: FactKeyDetailView }) {
  const t = useTranslations('pages.admin.skills.fact-keys');
  const intl = useIntl();
  const sourceLabel = (source: string) =>
    intl.messages[`${SOURCE_MESSAGE_PREFIX}${source}`] === undefined ? source : t(`source.${source}`);
  const details = detail.details;

  if (details === null) return null;

  const rows: ReadonlyArray<{ readonly label: string; readonly content: ReactNode }> = [
    { label: t('entry.description'), content: details.description ?? '' },
    { label: t('entry.value-list'), content: details.valueList.join(', ') },
    {
      label: t('entry.subfields'),
      content: details.subfields.map((subfield) => (
        <div key={subfield.name}>
          {[subfield.name, t(`value-type.${subfield.valueType}`), subfield.unit]
            .filter((part) => part !== null)
            .join(' · ')}
        </div>
      )),
    },
    {
      label: t('entry.sources'),
      content: details.sources.map((source) => <div key={source}>{sourceLabel(source)}</div>),
    },
    {
      label: t('entry.photo-derivable'),
      content:
        details.photo === null
          ? ''
          : `${t(`entry.photo.${details.photo.derivable}`)}${details.photo.limit === null ? '' : `: ${details.photo.limit}`}`,
    },
    { label: t('entry.consequence-when-missing'), content: details.consequenceWhenMissing ?? '' },
    { label: t('entry.collection-hint'), content: details.collectionHint ?? '' },
    {
      label: t('entry.rule-references'),
      content: details.ruleReferences.map((reference) => (
        <div key={reference.reqId}>{`${reference.reqId} · ${reference.rule} · ${reference.usage}`}</div>
      )),
    },
  ].filter((row) => (Array.isArray(row.content) ? row.content.length > 0 : row.content !== ''));

  if (rows.length === 0) return null;

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium text-text-primary">{t('entry.details-heading')}</h3>
      <dl className="space-y-3 rounded-md bg-canvas p-4 text-sm">
        {rows.map((row) => (
          <Fact key={row.label} label={row.label}>
            {row.content}
          </Fact>
        ))}
      </dl>
      <p className="text-sm text-text-tertiary">{t('entry.details-hint')}</p>
    </section>
  );
}
